<template>
  <div class="products-quantities-selector">
    <div :class="[{ multiple: multiple }, `products-matrix ${matrixType}`]">
      <div class="products">
        <div v-if="increaseQuantitiesNotAllowed" class="warning-banner">
          {{ $t('matrix.increase_quantities_is_not_allowed') }}
        </div>
        <!-- id is used to calculate sticky position of multiple sticky elements -->
        <div
          class="dates-selector-cnt"
          id="matrixRelatedOrdersSelector"
          :style="`top: ${matrixRelatedOrdersStickyOffset}px`">
          <MasterOrderSelector
            v-if="multiple"
            :orders="multipleOrdersSelectorOrders"
            :enable-add-button="!isExistingOrder"
            :enable-edit-button="!isExistingOrder"
            :enable-clone-button="!isExistingOrder"
            :enable-delete-button="!isExistingOrder && matrixOrders.length > 1"
            :selected-order-is-valid="true"
            v-on:select-order="selectOrder"
            v-on:edit-order="toggleDatePicker"
            v-on:add-new-order="addNewOrder"
            v-on:clone-order="cloneOrder"
            v-on:delete-order="deleteOrder" />
          <div
            v-if="showDatePicker"
            class="products-quantities-order-date-cnt"
            :style="{ left: datepickerLeftPosition }">
            <InputValidator v-model="selectedOrderFormattedDate">
              <Datepicker
                :id="`order-${selectedOrderIndex}-date`"
                v-model="selectedOrderFormattedDate"
                :format="formatDateNoUTC"
                :start-date="minimumDate"
                :max-date="maximumDate"
                :min-date="minimumDate"
                :locale="userLocale"
                :disabled-dates="disabledDates"
                :placeholder="$t(`matrix.ship_date_placeholder`)"
                :inline="true"
                :enable-time-picker="false"
                :dark="isDark()"
                :month-change-on-scroll="false"
                :auto-apply="true"
                @update:model-value="onOrderDateChange" />
            </InputValidator>
          </div>
        </div>
        <!-- id is used to calculate sticky position of multiple sticky elements -->
        <div
          :class="[{ multiple: multiple }]"
          :style="`top: ${mainGroupGenderNavBarStickyOffset}px`"
          class="navbar-background"
          id="quantitiesNavBar"
          v-if="gendersMainGroupsProducts.length">
          <QuantitiesNavBar
            ref="quantitiesNavBar"
            :items="navBarItems"
            :show-quantities="isNotAvailability"
            v-on:selected="selectAvailableMainGroupsGenders" />
        </div>

        <div v-if="loading" class="loader"></div>
        <div
          v-else
          v-for="genderMainGroupProducts in gendersMainGroupsProducts.filter((item) => item.selected)"
          :key="genderMainGroupProducts.mainGroup + genderMainGroupProducts.gender"
          class="product-groups-cnt"
          :class="[{ availability: !isNotAvailability }]">
          <div
            v-for="(
              productGroupFabricProducts, productGroupIndex
            ) in genderMainGroupProducts.productGroupsFabricsProducts"
            :key="productGroupIndex + productGroupFabricProducts.productGroup.id"
            class="product-group-cnt">
            <ProductGroupMatrixHeader
              :product-group-fabric-products="productGroupFabricProducts"
              :show-remove-all-button="isNotAvailability"
              :show-totals="isNotAvailability"
              v-on:remove-all-quantities="removeAllProductGroupFabricProducts(productGroupFabricProducts)" />
            <!-- ToDo create a product-group-matrix component -->
            <div
              v-if="!productGroupFabricProducts.collapsed"
              class="product-group-body"
              data-cy="product-group-size-matrix">
              <div class="matrix-table">
                <!-- sizes row -->
                <div class="row header" :style="`top: ${matrixSizesBarStickyOffset}px`">
                  <div class="column first-column">
                    <div class="size-selector-cnt">
                      <div class="size-selector">
                        <div class="allways-size">us</div>
                        <select
                          :name="'size-selector' + productGroupIndex"
                          v-model="selectedSize"
                          class="with-arrow with-focus-bg no-border">
                          <option v-for="size in availableCountrySizes" :key="size">
                            {{ size }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div class="column" v-for="size in productGroupFabricProducts.sizes" :key="size.id">
                    <div class="size-cnt">
                      <div class="us">
                        {{ size.us }}
                      </div>
                      <div>
                        {{ size[selectedSize] }}
                      </div>
                    </div>
                  </div>
                  <div v-if="isNotAvailability" class="column last-column"></div>
                </div>
                <!-- variant rows -->
                <div
                  v-for="(fabricProducts, fabricIndex) in productGroupFabricProducts.fabricsProducts"
                  :key="fabricProducts.fabric.id"
                  class="row product-fabric"
                  :class="[{ 'pre-order-disabled': fabricProducts.disabled }]">
                  <div class="column first-column">
                    <!-- ToDo create a product-variant-matrix-description component -->
                    <div
                      class="product-fabric-img"
                      v-on:click="
                        showProductModelDetailsPopup(fabricProducts.matrixProducts.find(Boolean) as MatrixProduct)
                      ">
                      <img :src="fabricProducts.imageUrl" />
                      <div class="expand-image-btn"></div>
                    </div>
                    <div class="product-fabric-text">
                      <p class="strong">
                        {{ fabricProducts.fabric.name }}
                      </p>
                      <p>
                        {{ fabricProducts.description }}
                      </p>
                    </div>
                    <div v-if="fabricProducts.launchDate" class="product-launch-text">
                      <p class="strong">
                        {{ $t('matrix.product_launch_date') }}
                      </p>
                      <p>{{ parseUtcDateToLocaleDate(fabricProducts.launchDate) }}</p>
                    </div>
                  </div>
                  <div
                    class="column"
                    v-for="(matrixProduct, index) in fabricProducts.matrixProducts"
                    :key="`product-${productGroupIndex}-${fabricIndex}-${index}`">
                    <!-- ToDo create a product-matrix-input component -->
                    <div v-if="matrixProduct" class="product">
                      <div
                        v-if="showAvailability"
                        class="product-header"
                        :class="[matrixProduct.availabilityInfo?.availabilityColorClass]">
                        <span
                          v-if="matrixProduct.availabilityInfo?.availabilityText"
                          :title="matrixProduct.availabilityInfo.availabilityText">
                          {{ matrixProduct.availabilityInfo.availabilityText }}
                        </span>
                      </div>
                      <div v-if="isNotAvailability" class="product-total">
                        <input
                          :id="`product-${productGroupIndex}-${fabricIndex}-${index}`"
                          :disabled="isInputDisabled(matrixProduct)"
                          type="number"
                          :value="matrixProduct.total || null"
                          :placeholder="getInputPlaceholder(matrixProduct)"
                          v-on:change="partiallyRecalculateTotals(matrixProduct, $event)" />
                      </div>
                    </div>
                    <div v-if="!matrixProduct"></div>
                  </div>
                  <div v-if="isNotAvailability" class="column last-column">
                    <span class="strong">{{ fabricProducts.total }}</span>
                  </div>
                  <div v-if="fabricProducts.disabled" class="pre-order-disabled-legend">
                    <p v-if="isLaunchDateAfterSelectedDate(fabricProducts.launchDate)">
                      {{ $t('matrix.pre_order_phase_in_date_disabled') }}
                    </p>
                    <p v-else-if="isCarryOverDisabled(fabricProducts.launchDate)">
                      {{ $t('matrix.pre_order_carry_over_disabled') }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ToDo Create a totals Component -->
          <div class="totals-cnt">
            <ul v-if="showAvailability" class="availability-legends">
              <li class="green-dot">
                <p>{{ $t('matrix.availability_label_item_available') }}</p>
              </li>
              <li class="red-dot">
                <p>{{ $t('matrix.availability_label_on_backorder') }}</p>
              </li>
              <li class="special-red-dot">
                <p>{{ $t('matrix.availability_label_not_available') }}</p>
              </li>
              <li class="orange-dot">
                <p>{{ $t('matrix.availability_label_low_availability') }}</p>
              </li>
            </ul>
            <ul class="totals">
              <li v-for="mainGroupTotal in mainGroupsTotals" :key="mainGroupTotal.title">
                <p>
                  {{ $t('matrix.total') + ' ' + $t(`matrix.tab_name_${mainGroupTotal.title}`) }}
                </p>
                <span>{{ mainGroupTotal.total }}</span>
              </li>
              <li class="clear">
                <p>{{ $t('matrix.total') }}</p>
                <span>{{ selectedOrder?.total }}</span>
              </li>
            </ul>
          </div>
          <div v-if="multiple && matrixOrders.length > 1" class="total-all-cnt">
            <ul class="totals">
              <li class="total-all">
                <p>{{ $t('matrix.total_all') }}</p>
                <span>{{ total }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showProductModelDetails">
      <NotificationMessage :size="'full'" class="popup-new" v-on:close="closeProductModelDetailsPopup">
        <ViewProductModelDetails
          :hide-select-button="true"
          :product-model="showProductModelDetails"
          v-on:close="closeProductModelDetailsPopup" />
      </NotificationMessage>
    </div>
  </div>
</template>

<script lang="ts">
import NotificationMessage from '@/components/common/NotificationMessage.vue';
import QuantitiesNavBar from '@/components/common/QuantitiesNavBar.vue';
import { useBPLogger } from '@/composables/business-process-logger';
import { globalConstants } from '@/constants/global.constants';
import { CARRY_OVER_RESTRICTED_LEGAL_ENTITIES, CARRY_OVER_UNAVAILABLE_DAYS } from '@/constants/order.constants';
import { ordersMixins } from '@/mixins/orders.mixins';
import { AllowableProductModel } from '@/models/allowable-product-model.model';
import { MatrixOrder } from '@/models/matrix-order.model';
import { ProductImagesService } from '@/services/product-images.service';
import { WarehouseService } from '@/services/warehouse.service';
import { AccountModel, LegalEntityEnum, OrderTypeDto, ProductGender, ProductSize } from '@onrunning/backstage-models';
import structuredClone from '@ungap/structured-clone';
import Datepicker from '@vuepic/vue-datepicker';
import { cloneDeep } from 'lodash';
import { defineComponent, toRaw, type PropType } from 'vue';
import { useFormat } from '../../composables/format';
import { useLocale } from '../../composables/locale';
import { toRawDeep } from '../../composables/to-raw-deep';
import { accountMixins } from '../../mixins/account.mixins';
import { utilsMixins } from '../../mixins/utils.mixins';
import { BackstageTheme } from '../../models/backstage-theme.enum';
import { FabricProducts } from '../../models/fabric-products.model';
import { GenderMainGroupProducts } from '../../models/gender-main-group-products.model';
import { MasterOrderSelectorModel } from '../../models/master-order-selector.model';
import { MatrixProduct } from '../../models/matrix-product.model';
import { MatrixType } from '../../models/matrix-type.enum';
import { ProductAvailabilityInfo } from '../../models/product-availability-info';
import { Fabric } from '../../models/product-fabric.model';
import { ProductGroupFabricsProducts } from '../../models/product-group-fabric-products.model';
import { ProductGroup } from '../../models/product-group.model';
import { ProductMainGroups } from '../../models/product-main-group.enum';
import { ProductsFilter } from '../../models/products-filter.model';
import { ProductsMatrix } from '../../models/products-matrix.model';
import { ProductsSizes } from '../../models/products-sizes.model';
import { ProductsMatrixService } from '../../services/products-matrix.service';
import { useUserStore } from '../../stores/user';
import {
  addDateDuration,
  isDateAfter,
  isDateBefore,
  today,
  toLocalMidnightDate,
  toPlainDate,
} from '../../utils/temporalUtils';
import InputValidator from '../common/input-validator.vue';
import type { NavBarItem } from '../common/QuantitiesNavBar.vue';
import MasterOrderSelector from '../orders/master-order-selector.vue';
import ViewProductModelDetails from '../products/ViewProductModelDetails.vue';
import ProductGroupMatrixHeader from './ProductGroupMatrixHeader.vue';

type DisabledDates = (date: Date) => boolean;

/* eslint-disable vue/no-mutating-props */
// ToDo mutating props on vue is consider an antipattern
// once we do a larger refactor to the matrix we can correctly start emitting the changes instead
const ProductsQuantitiesSelector = defineComponent({
  name: 'products-quantities-selector',
  components: {
    InputValidator,
    ViewProductModelDetails,
    NotificationMessage,
    MasterOrderSelector,
    Datepicker,
    QuantitiesNavBar,
    ProductGroupMatrixHeader,
  },
  props: {
    matrixOrders: {
      type: Array<MatrixOrder>,
      default: [],
    },
    selectedDate: {
      type: Date,
      required: false,
    },
    selectedOrderTypeInfo: {
      type: Object as PropType<OrderTypeDto>,
      required: false,
    },
    matrixType: {
      type: String,
      required: true,
    },
    account: {
      type: Object as PropType<AccountModel>,
      required: true,
    },
    defaultSize: {
      type: String,
      default: 'eu',
    },
    minimumDate: {
      type: Date,
      default: new Date(),
    },
    maximumDate: {
      type: Date,
      default: new Date(),
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    isExistingOrder: {
      type: Boolean,
      default: false,
    },
    orderNumber: {
      type: String,
      default: undefined,
    },
    selectedProductModels: {
      type: Array,
      default: undefined,
    },
  },
  emits: ['update:matrixOrders', 'delete-draft', 'matrix-touched', 'all-orders-are-valid'],

  setup() {
    const { isSalesRep, accountIsDistributor } = useUserStore();
    const { userLocale } = useLocale();
    const { formatDateToLocaleStringIgnoringTime, formatDateNoUTC, formatDate, parseUtcDateToLocaleDate } = useFormat();
    const { pageViewBPLog } = useBPLogger();
    const isDisableInputOnNoStockActivated = import.meta.env.VITE_APP_disableInputOnNoStock === 'true';
    const excludedWarehouses = ['TES-KN-STK'];

    return {
      userLocale,
      excludedWarehouses,
      isSalesRep,
      accountIsDistributor,
      formatDateToLocaleStringIgnoringTime,
      parseUtcDateToLocaleDate,
      formatDateNoUTC,
      formatDate,
      pageViewBPLog,
      isDisableInputOnNoStockActivated,
    };
  },
  data() {
    const { userProfile } = useUserStore();

    return {
      warehouseService: new WarehouseService(),
      matrixService: new ProductsMatrixService(),
      productImagesService: new ProductImagesService(),
      availableCountrySizes: globalConstants.sizeTypes.filter((size) => size !== globalConstants.alwaysVisibleSizeType),
      loading: true,
      fetchingMatrix: false,
      availableMainGroupsGenders: new Array<any>(),
      filtering: true,
      productsMatrix: new ProductsMatrix(),
      gendersMainGroupsProducts: new Array<GenderMainGroupProducts>(),
      mainGroupsTotals: new Array<any>(),
      productModels: new Array<string>(),
      total: 0,
      lowAvailability: 0,
      veryLowAvailability: 0,
      noAvailability: 0,
      selectedSize: '' as keyof ProductSize,
      isNotAvailability: this.matrixType !== MatrixType.availability,
      showAvailability: true,
      disabledDates: (() => true) as DisabledDates,
      adaptedFilters: false,
      ordersLoaded: false,
      showDatePicker: false,
      selectedCollectionNames: new Array<string>(),
      selectedProducts: new Array<MatrixProduct>(),
      datepickerLeftPosition: '0px',
      showProductModelDetails: undefined as AllowableProductModel | undefined,
      userProfile,
      matrixRelatedOrdersStickyOffset: 0,
      matrixSizesBarStickyOffset: 0,
      mainGroupGenderNavBarStickyOffset: 0,
      sizeGroups: [] as Array<any>, // ToDo change to new sizeGroupDto type
      page_visit_start: 0,
      updatedMatrixOrders: cloneDeep(this.matrixOrders),
    };
  },
  computed: {
    increaseQuantitiesNotAllowed(): boolean {
      return this.orderNumber !== undefined;
    },
    selectedOrder(): MatrixOrder {
      return this.updatedMatrixOrders[this.selectedOrderIndex] ?? this.updatedMatrixOrders[0];
    },
    selectedOrderFormattedDate: {
      get() {
        return this.selectedOrder.date ? toPlainDate(this.selectedOrder.date) : null;
      },
      set(value: Date) {
        this.selectedOrder.date = value;
      },
    },
    selectedOrderIndex(): number {
      return this.updatedMatrixOrders?.findIndex((matrixOrder) => matrixOrder.selected) ?? 0;
    },
    multipleOrdersSelectorOrders(): MasterOrderSelectorModel[] {
      return this.updatedMatrixOrders.map((matrixOrder) => ({
        id: matrixOrder.orderId ?? '',
        title: matrixOrder.date
          ? this.formatDateToLocaleStringIgnoringTime(matrixOrder.date)
          : this.$t('matrix.ship_date_placeholder').toString(),
        subTitle: `${matrixOrder.total.toString()} ${this.$t('viewOrder.items')}`,
        selected: matrixOrder.selected,
      }));
    },
    navBarItems(): NavBarItem[] {
      return this.gendersMainGroupsProducts.map((gendersMainGroupsProduct) => {
        return {
          title: this.$t(`matrix.tab_name_${gendersMainGroupsProduct.mainGroup}_${gendersMainGroupsProduct.gender}`),
          id: `${gendersMainGroupsProduct.mainGroup}_${gendersMainGroupsProduct.gender}`,
          total: gendersMainGroupsProduct.total,
          selected: gendersMainGroupsProduct.selected,
        };
      });
    },
    canDisableFabric(): boolean {
      return (
        !this.selectedOrder.date ||
        (this.matrixType === MatrixType.preorder && !this.accountIsDistributor() && this.isExistingOrder === false)
      );
    },
    isCarryOverRestrictedLegalEntity(): boolean {
      return CARRY_OVER_RESTRICTED_LEGAL_ENTITIES.includes(this.account.legalEntity as LegalEntityEnum);
    },
  },
  beforeMount() {
    this.fetchInitialData();
  },
  mounted() {
    this.page_visit_start = new Date().getTime();
    window.addEventListener('resize', this.setStickyOffsets);
  },
  updated() {
    this.setStickyOffsets();
  },
  unmounted() {
    const visitDuration = new Date().getTime() - this.page_visit_start;
    console.log(this.pageViewBPLog(true, { page_name: 'products-quantities-selector' }, visitDuration));
    window.removeEventListener('resize', this.setStickyOffsets);
  },
  watch: {
    selectedDate(newVal: Date, oldVal: Date) {
      if (newVal.valueOf() !== oldVal.valueOf()) {
        this.onOrderDateChange();
      }
    },
  },
  methods: {
    ...utilsMixins,
    ...accountMixins,
    ...ordersMixins,
    async fetchInitialData() {
      const warehouse = await this.warehouseService.getWarehouse(this.account.id);

      if (!warehouse) {
        this.$snotify.error(this.$t('global.error'));

        return;
      }

      await this.fetchMatrixProductsWithNewSelector();

      this.noAvailability = warehouse.thresholds.outOfStock;
      this.lowAvailability = warehouse.thresholds.lowStock;
      this.veryLowAvailability = warehouse.thresholds.veryLowStock;

      this.ordersLoaded = true;

      this.selectedSize = this.defaultSize as keyof ProductSize;
      this.showAvailability =
        this.matrixType !== MatrixType.warranty &&
        this.matrixType !== MatrixType.return &&
        this.matrixType !== MatrixType.preorder;

      if (this.minimumDate) {
        this.disabledDates = (target: Date) => {
          return target < toLocalMidnightDate(this.minimumDate ?? today());
        };
      }

      if (this.maximumDate) {
        this.disabledDates = (target: Date) => {
          const targetPlainDate = toPlainDate(target);

          return isDateAfter(targetPlainDate, addDateDuration(toPlainDate(this.maximumDate), { days: 1 }));
        };
      }
    },
    cloneOrder(index: number) {
      const clone = Object.assign(new MatrixOrder(), {
        matrixProducts: structuredClone(toRawDeep(this.updatedMatrixOrders?.[index].matrixProducts)),
        date: this.updatedMatrixOrders?.[index].date,
        total: this.updatedMatrixOrders?.[index].total,
        selected: false,
      });

      this.addNewMatrixOrder(clone);
      this.$emit('update:matrixOrders', this.updatedMatrixOrders);
    },
    deleteOrder(index: number) {
      this.showDatePicker = false;

      if (this.updatedMatrixOrders.length === 1) {
        this.addNewOrder();
      }

      const orderToBeDeleted = this.updatedMatrixOrders[index];

      if (orderToBeDeleted?.orderId) {
        this.$emit('delete-draft', orderToBeDeleted.orderId);
      }

      this.updatedMatrixOrders?.splice(index, 1);

      if (index === this.selectedOrderIndex) {
        this.updatedMatrixOrders[0].selected = true;

        this.updateMatrix();
      }

      this.emitOrdersValidation();

      this.$emit('update:matrixOrders', this.updatedMatrixOrders);
    },
    addNewOrder() {
      this.addNewMatrixOrder();
      this.$emit('update:matrixOrders', this.updatedMatrixOrders);
    },
    toggleDatePicker() {
      this.showDatePicker = !this.showDatePicker;

      const element = document.getElementById(`master-order-item-${this.selectedOrderIndex}`);

      if (element && this.showDatePicker) {
        const rect = element.getBoundingClientRect();

        this.datepickerLeftPosition = `${rect.left}px`;
      }
    },
    addNewMatrixOrder(orderMatrix?: Partial<MatrixOrder>) {
      this.updatedMatrixOrders.push({
        total: 0,
        matrixProducts: [],
        date: undefined,
        ...orderMatrix,
        selected: false,
      });

      this.emitOrdersValidation();
    },
    async fetchMatrixProductsWithNewSelector(): Promise<void> {
      if (this.selectedProductModels && this.selectedProductModels.length > 0) {
        this.productModels = this.selectedProductModels as string[];
      } else {
        return;
      }
    },
    checkDisableWarehouseFlagAndMatrixType(): boolean {
      return (
        !this.isDisableInputOnNoStockActivated ||
        !this.showAvailability ||
        this.matrixType === MatrixType.preorder ||
        this.excludedWarehouses.includes(this.productsMatrix.warehouse ?? '')
      );
    },
    isInputDisabled(matrixProduct: MatrixProduct): boolean {
      if (this.checkDisableWarehouseFlagAndMatrixType()) {
        return false;
      }
      if (matrixProduct.total) {
        return false;
      }

      return (
        matrixProduct.availabilityInfo?.availabilityText === this.$t('config.no_availability_label') ||
        matrixProduct.availabilityInfo?.availabilityText === this.$t('matrix.availability_tbd').toString()
      );
    },
    getInputPlaceholder(matrixProduct: MatrixProduct): string {
      return this.isInputDisabled(matrixProduct) ? '' : '0';
    },

    updateMatrixOrdersWithProducts() {
      this.updatedMatrixOrders.forEach((order) => {
        order.matrixProducts = order.matrixProducts.map((product) => {
          let updatedProduct = product;
          const productInfo = this.productsMatrix.products.find((prod) => prod.productId === product.productId);

          if (productInfo) {
            updatedProduct = {
              ...product,
              availabilityInfo: productInfo.availabilityInfo,
              picUrl: productInfo.picUrl,
              productName: productInfo.productName,
              productGroupName: productInfo.productGroupName,
              sizeId: productInfo.sizeId,
            };
          }

          return updatedProduct;
        });
      });
    },

    async fetchMatrixProducts(productsFilters: ProductsFilter[]): Promise<void> {
      if (!this.fetchingMatrix) {
        this.fetchingMatrix = true;
        this.loading = true;

        let selectedProductGroupNames = new Set<string>();

        this.mainGroupsTotals = [];
        this.availableMainGroupsGenders = [];
        this.selectedCollectionNames = [];

        productsFilters.forEach((productFilter) => {
          if (productFilter.selected) {
            const selectedGroups = productFilter.productGroups
              .filter((productGroup) => productGroup.selected)
              .map((productGroup) => productGroup.name);

            if (selectedGroups.length) {
              this.selectedCollectionNames.push(productFilter.name);
            }

            selectedProductGroupNames = new Set([
              ...selectedProductGroupNames,
              ...selectedGroups,
              ...(this.updatedMatrixOrders[0]?.matrixProducts || []).map(({ productGroupName }) => productGroupName),
            ]);
          }
        });

        this.productsMatrix = await this.matrixService.getProductsMatrix(
          Array.from(selectedProductGroupNames),
          this.account.id,
          this.updatedMatrixOrders[0]?.date as Date,
          this.matrixType as MatrixType,
          this.matrixType === MatrixType.preorder
            ? this.selectedCollectionNames.filter((collection) => collection !== '')
            : undefined,
          this.orderNumber,
        );

        if (!this.productsMatrix?.sizeGroups?.length || !this.productsMatrix?.products?.length) {
          console.error({
            msg: 'matrix#fetchMatrixProducts didnt receive a products matrix from api',
          });

          return this.$snotify.error(this.$t('global.error'));
        }

        const mainGroupsGenders = new Set<string>();
        const mainGroups = new Set<string>();

        this.productsMatrix.products.forEach((product) => {
          const mainGroup = product.mainGroup;
          const genderNumber = product.gender;

          const mainGroupGender =
            mainGroup === ProductMainGroups.accessories ? mainGroup : `${mainGroup}_${genderNumber}`;

          mainGroupsGenders.add(mainGroupGender);
          mainGroups.add(mainGroup);
        });

        this.sizeGroups = this.productsMatrix.sizeGroups;

        this.mainGroupsTotals = Array.from(mainGroups).map((mainGroup) => ({
          total: 0,
          title: mainGroup,
        }));

        this.availableMainGroupsGenders = Array.from(mainGroupsGenders)
          .sort((a: string, b: string) => b.localeCompare(a))
          .map((mainGroupGender) => ({ total: 0, title: mainGroupGender, selected: false }));

        if (this.availableMainGroupsGenders[0]) {
          this.availableMainGroupsGenders[0].selected = true;
        }

        this.setGendersMainGroupsProducts();
        this.updateMatrix();
        // TODO: The below function does not work as there is a if condition that is always false. We need to look back at this.
        this.calculateOrdersTotals();

        this.updateMatrixOrdersWithProducts();

        this.adaptedFilters = false;
        this.fetchingMatrix = false;
        this.loading = false;
        this.filtering = false;

        window.scrollTo(0, 0);
        this.$emit('update:matrixOrders', this.updatedMatrixOrders);
      }
    },
    selectOrder(index: number) {
      if (this.selectedOrderIndex === index) {
        return;
      }

      this.showDatePicker = false;

      this.selectedOrder.selected = false;
      this.updatedMatrixOrders[index].selected = true;

      this.updateMatrix();
      this.$emit('update:matrixOrders', this.updatedMatrixOrders);
    },
    selectAvailableMainGroupsGenders(id: string) {
      const mainGroup = id.split('_')[0];
      const gender = Number(id.split('_')[1]);

      this.gendersMainGroupsProducts.map(
        (groupGender) => (groupGender.selected = groupGender.mainGroup === mainGroup && groupGender.gender === gender),
      );
      (document.getElementsByTagName('body')[0] as Element).scrollIntoView({ behavior: 'smooth' });
    },
    setGendersMainGroupsProducts() {
      let first = true;

      this.gendersMainGroupsProducts = new Array<GenderMainGroupProducts>();

      this.availableMainGroupsGenders.forEach((availableMainGroupGender) => {
        const genderMainGroupProducts = new GenderMainGroupProducts();

        genderMainGroupProducts.selected = first;
        const mainGroupGender = availableMainGroupGender.title.split('_');

        genderMainGroupProducts.mainGroup = mainGroupGender[0];
        genderMainGroupProducts.gender = mainGroupGender.length > 1 ? parseInt(mainGroupGender[1], 10) : -1;
        genderMainGroupProducts.total = 0;

        genderMainGroupProducts.productGroupsFabricsProducts = this.productsMatrix.products
          .filter(
            (product) =>
              product.mainGroup === genderMainGroupProducts.mainGroup &&
              (!(genderMainGroupProducts.gender in ProductGender) || product.gender === genderMainGroupProducts.gender),
          )
          .reduce((productGroupsFabricsProducts: ProductGroupFabricsProducts[], product: MatrixProduct) => {
            let productGroupFabricsProducts = productGroupsFabricsProducts.find(
              (item) => item.productGroup.id === product.productGroupId && item.gender === product.gender,
            ) as ProductGroupFabricsProducts;

            if (!productGroupFabricsProducts) {
              productGroupFabricsProducts = new ProductGroupFabricsProducts();
              const productGroup = this.productsMatrix.productGroups.find(
                (group) => group.id === product.productGroupId,
              ) as ProductGroup;

              productGroupFabricsProducts.productGroup = productGroup;
              productGroupFabricsProducts.fabricsProducts = new Array<FabricProducts>();
              productGroupFabricsProducts.gender = product.gender;
              productGroupFabricsProducts.total = 0;
              productGroupFabricsProducts.collapsed = false;

              productGroupsFabricsProducts.push(productGroupFabricsProducts);
            }

            let fabricProducts = productGroupFabricsProducts.fabricsProducts.find(
              (fabricProduct) => fabricProduct.fabric.id === product.fabricId,
            );

            if (!fabricProducts) {
              fabricProducts = new FabricProducts();
              fabricProducts.fabric = this.productsMatrix.fabrics.find(
                (fabric) => fabric.id === product.fabricId,
              ) as Fabric;
              fabricProducts.matrixProducts = new Array<MatrixProduct>();
              fabricProducts.total = 0;

              productGroupFabricsProducts.fabricsProducts.push(fabricProducts);
            }

            if (!product.total) {
              product.total = 0;
            }

            if (this.selectedOrder.matrixProducts) {
              const productWithQuantity = this.selectedOrder.matrixProducts.find(
                (prod) => prod.productName === product.productName && prod.sizeId === product.sizeId,
              );

              if (productWithQuantity) {
                product.total = productWithQuantity.total;
              }
            }

            product.availabilityInfo = this.getProductAvailabilityInfo(product);

            fabricProducts.matrixProducts.push(product);

            if (!fabricProducts.imageUrl && product.picUrl) {
              fabricProducts.imageUrl = this.getBoxImageProxyUrl(product.picUrl);
            }

            if (!fabricProducts.description) {
              fabricProducts.description = this.getFabricDescription(
                product.productGroupNumber,
                productGroupFabricsProducts.productGroup.code,
                fabricProducts.fabric.code,
              );
            }

            if (!fabricProducts.launchDate && product.launchDate) {
              fabricProducts.launchDate = product.launchDate;
            }

            return productGroupsFabricsProducts;
          }, new Array<ProductGroupFabricsProducts>());

        genderMainGroupProducts.productGroupsFabricsProducts.forEach((productGroupFabricsProducts) => {
          const fabricMatrixProducts = productGroupFabricsProducts.fabricsProducts.flatMap(
            (fabricsProducts) => fabricsProducts.matrixProducts,
          );
          const fabricMatrixProductsSizes = fabricMatrixProducts.map((matrixProduct) => matrixProduct.sizeId);
          const fabricMainGroup = fabricMatrixProducts.find((product) => !!product)?.mainGroup as ProductMainGroups;

          productGroupFabricsProducts.sizes = import.meta.env.VITE_APP_enableSizeGroups
            ? this.sizeGroups
                .find((sizeGroup) => sizeGroup.sizeGroup === fabricMatrixProducts[0].sizeGroup)
                ?.sizes.filter((productSize: ProductSize) => fabricMatrixProductsSizes.includes(productSize.name))
            : this.getMaingroupGenderSizes(fabricMainGroup, productGroupFabricsProducts.gender).filter((productSize) =>
                fabricMatrixProductsSizes.includes(productSize.name),
              );

          productGroupFabricsProducts.fabricsProducts.forEach((fabricProducts) => {
            const products = Object.assign(new Array<MatrixProduct>(), fabricProducts.matrixProducts);

            fabricProducts.matrixProducts = new Array<MatrixProduct>();
            productGroupFabricsProducts.sizes?.forEach((size) => {
              fabricProducts.matrixProducts.push(products.find((item) => item.sizeId === size.id) as MatrixProduct);
            });
          });
        });

        first = false;
        this.gendersMainGroupsProducts.push(genderMainGroupProducts);
      });

      this.recalculateAssociatedTotals(undefined, undefined, true);
    },
    getMaingroupGenderSizes(mainGroup: ProductMainGroups, gender: ProductGender) {
      return (
        this.productsMatrix.sizes.find(
          (size) => size.productGender === gender && size.mainGroup === mainGroup,
        ) as ProductsSizes
      )?.sizes;
    },
    removeAllProductGroupFabricProducts(productGroupFabricsProducts: ProductGroupFabricsProducts) {
      productGroupFabricsProducts.fabricsProducts.forEach((fabricProducts) => {
        fabricProducts.matrixProducts.forEach((product) => {
          if (product) {
            product.total = 0;
            this.setQuantityToProductOnSelectedMatrixOrder(product);
          }
        });
      });
      this.recalculateAssociatedTotals(undefined, undefined, true);
      this.$emit('update:matrixOrders', this.updatedMatrixOrders);
    },
    recalculateAssociatedTotals(product?: MatrixProduct, event?: KeyboardEvent, calculateAll = false) {
      if (product && event) {
        const productTotal = parseInt((event.currentTarget as HTMLInputElement).value, 10);

        product.total = !isNaN(productTotal) && productTotal > 0 ? productTotal : 0;
      }

      this.selectedOrder.total = 0;
      this.mainGroupsTotals.map((mainGroupTotals) => (mainGroupTotals.total = 0));

      this.gendersMainGroupsProducts.forEach((genderMainGroupProducts) => {
        const currentMainGroupTotals = this.mainGroupsTotals.find(
          (mainGroupTotals) => mainGroupTotals.title === genderMainGroupProducts.mainGroup,
        );

        if (genderMainGroupProducts.selected || calculateAll) {
          genderMainGroupProducts.total = 0;
          genderMainGroupProducts.productGroupsFabricsProducts.forEach(
            (productGroupsFabricsProducts: ProductGroupFabricsProducts) => {
              if (
                (product && product.productGroupId === productGroupsFabricsProducts.productGroup.id) ||
                calculateAll
              ) {
                productGroupsFabricsProducts.total = 0;
                productGroupsFabricsProducts.fabricsProducts.forEach((fabricProducts) => {
                  if ((product && product.fabricId === fabricProducts.fabric.id) || calculateAll) {
                    fabricProducts.total = 0;

                    if (!fabricProducts.disabled) {
                      fabricProducts.matrixProducts.forEach((matrixProduct) => {
                        if (matrixProduct) {
                          fabricProducts.total += matrixProduct.total;
                        }
                      });
                    }
                  }

                  productGroupsFabricsProducts.total += fabricProducts.total;
                });
              }

              genderMainGroupProducts.total += productGroupsFabricsProducts.total;
            },
          );
        }

        currentMainGroupTotals.total += genderMainGroupProducts.total;
        this.selectedOrder.total += genderMainGroupProducts.total;
      });

      this.total = 0;
      this.updatedMatrixOrders.forEach((order) => {
        this.total += order.total;
      });

      this.emitOrdersValidation();
    },
    partiallyRecalculateTotals(product: MatrixProduct, event: any) {
      let newValue = parseInt(event.target.value, 10);
      if (isNaN(newValue) || this.isInputDisabled(product)) {
        newValue = 0;
        event.target.value = 0;
      }

      if (this.orderNumber && product.originalQuantity && product.originalQuantity < newValue) {
        event.target.value = product.total;

        return;
      }

      const addition = newValue - product.total;

      product.total = newValue;

      const selectedGenderMainGroupProducts = this.gendersMainGroupsProducts.find(
        (genderMainGroupProducts) => genderMainGroupProducts.selected,
      ) as GenderMainGroupProducts;

      const selectedProductGroupsFabricsProducts = selectedGenderMainGroupProducts.productGroupsFabricsProducts.find(
        (productGroupsFabricsProduct) =>
          product.productGroupId === productGroupsFabricsProduct.productGroup.id &&
          product.gender === productGroupsFabricsProduct.gender,
      ) as ProductGroupFabricsProducts;

      const selectedFabricProducts = selectedProductGroupsFabricsProducts.fabricsProducts.find(
        (fabricProduct) => product?.fabricId === fabricProduct.fabric.id,
      ) as FabricProducts;

      const selectedGenderMainGroupTotals = this.mainGroupsTotals.find(
        (mainGroupTotal) => mainGroupTotal.title === product.mainGroup,
      );

      selectedGenderMainGroupTotals.total += addition;
      selectedGenderMainGroupProducts.total += addition;
      selectedProductGroupsFabricsProducts.total += addition;
      selectedFabricProducts.total += addition;
      this.selectedOrder.total += addition;
      this.total += addition;

      this.setQuantityToProductOnSelectedMatrixOrder(product);

      this.emitOrdersValidation();
      this.$emit('matrix-touched', true);
      this.$emit('update:matrixOrders', this.updatedMatrixOrders);
    },
    emitOrdersValidation() {
      this.$emit(
        'all-orders-are-valid',
        this.updatedMatrixOrders.every((matrixOrder) => this.orderIsValid(matrixOrder)),
      );
    },
    orderIsValid(matrixOrder: MatrixOrder): boolean {
      return matrixOrder.total > 0 && !!matrixOrder.date;
    },
    calculateOrdersTotals() {
      if (this.adaptedFilters) {
        const availableProductIds = this.productsMatrix.products.map((product) => product.productId);

        this.total = 0;
        this.updatedMatrixOrders.forEach((order) => {
          order.total = order.matrixProducts.reduce(
            (total, product) => total + (availableProductIds.includes(product.productId) ? product.total : 0),
            0,
          );
          this.total += order.total;
        });
      }
    },
    checkMatrixPosition(event: KeyboardEvent) {
      if (!(
        (event.keyCode > 95 && event.keyCode < 106) ||
        (event.keyCode > 47 && event.keyCode < 58) ||
        event.keyCode === 8
      )) {
        // if not a number then bye bye
        event.preventDefault();
        event.stopPropagation();

        return false;
      }

      // check if the user press any arrow keys
      // ToDo navigate user trough the matrix when arrows get pressed
      switch (event.keyCode) {
        case 37: // left
          break;
        case 38: // up
          break;
        case 39: // right
          break;
        case 40: // down
          break;
      }

      return true;
    },
    getProductAvailabilityInfo(matrixProduct: MatrixProduct): ProductAvailabilityInfo {
      let availabilityText = `${this.$t('config.in_availability_label')}`;

      if (matrixProduct.availableQuantity > 0) {
        if (this.isSalesRep()) {
          availabilityText = matrixProduct.availableQuantity?.toString();
        } else {
          if (
            matrixProduct.availableQuantity <= this.lowAvailability &&
            matrixProduct.availableQuantity > this.veryLowAvailability
          ) {
            availabilityText = `${this.$t('config.low_availability_label')}`;
          } else if (
            matrixProduct.availableQuantity <= this.veryLowAvailability &&
            matrixProduct.availableQuantity > this.noAvailability
          ) {
            availabilityText = `${this.$t('config.very_low_availability_label')}`;
          }
        }
      } else {
        availabilityText = `${this.$t('config.no_availability_label')}`;
      }

      return {
        availabilityText,
        availabilityColorClass: this.getAvailabilityColorClass(matrixProduct.availableQuantity),
      };
    },
    getAvailabilityColorClass(availableQuantity: number): string {
      let availabilityColorClass = 'special-green';
      if (availableQuantity <= this.lowAvailability && availableQuantity > this.veryLowAvailability) {
        availabilityColorClass = 'special-orange';
      } else if (availableQuantity <= this.veryLowAvailability && availableQuantity > this.noAvailability) {
        availabilityColorClass = 'special-red';
      } else if (availableQuantity === this.noAvailability) {
        availabilityColorClass = 'red';
      }

      return availabilityColorClass;
    },
    isLaunchDateAfterSelectedDate(fabricLaunchDate: string | undefined): boolean {
      if (!fabricLaunchDate) {
        return true;
      }
      const selectedOrderDate = this.selectedOrder.date ?? today();
      const isAfterRequestedDeliveryDate = isDateAfter(fabricLaunchDate, selectedOrderDate);

      return isAfterRequestedDeliveryDate;
    },
    isLaunchDateBeforeSeasonStart(fabricLaunchDate: string | undefined): boolean {
      if (!fabricLaunchDate) {
        return true;
      }

      const selectedOrderDate = toPlainDate(this.selectedOrder.date ?? today());
      const seasonStart = toPlainDate(this.selectedOrderTypeInfo?.startDate ?? today());
      const carryOverStartDate = addDateDuration(seasonStart, { days: CARRY_OVER_UNAVAILABLE_DAYS });
      const isBeforeSeasonStart = isDateBefore(fabricLaunchDate, seasonStart);

      const isSelectedDateBeforeCarryOverStartDate = isDateBefore(selectedOrderDate, carryOverStartDate);

      return isBeforeSeasonStart && isSelectedDateBeforeCarryOverStartDate;
    },
    isCarryOverDisabled(fabricLaunchDate: string | undefined): boolean {
      if (!this.isCarryOverRestrictedLegalEntity) {
        return false;
      }

      return this.isLaunchDateBeforeSeasonStart(fabricLaunchDate);
    },
    isFabricDisabled(fabricLaunchDate: string | undefined): boolean {
      return (
        this.canDisableFabric &&
        (this.isLaunchDateAfterSelectedDate(fabricLaunchDate) || this.isCarryOverDisabled(fabricLaunchDate))
      );
    },
    resolveProductQuantity(
      fabricProduct: MatrixProduct,
      selectedOrderProduct: MatrixProduct | undefined,
      isDisabled: boolean,
    ) {
      if (!selectedOrderProduct) {
        fabricProduct.total = 0;

        return;
      }

      if (isDisabled) {
        fabricProduct.total = 0;

        return;
      }

      fabricProduct.total = selectedOrderProduct.total;
      fabricProduct.originalQuantity = fabricProduct.total.valueOf();
    },
    updateMatrix() {
      this.gendersMainGroupsProducts.forEach((genderMainGroupProducts) => {
        genderMainGroupProducts.productGroupsFabricsProducts.forEach((groupFabrics) => {
          groupFabrics.fabricsProducts.forEach((fabrics) => {
            const fabricLaunchDate = fabrics.launchDate;

            fabrics.disabled = this.isFabricDisabled(fabricLaunchDate);

            fabrics.matrixProducts.forEach((product) => {
              if (product) {
                const productWithQuantity = this.selectedOrder?.matrixProducts?.find(
                  (prod) => prod.productId === product.productId,
                );

                this.resolveProductQuantity(product, productWithQuantity, fabrics.disabled!);
              }
            });
          });
        });
      });

      this.recalculateAssociatedTotals(undefined, undefined, true);
    },
    cancel() {
      if (this.matrixType === MatrixType.warranty) {
        this.$router.push('/warranty-cases');
      } else if (this.matrixType === MatrixType.return) {
        this.$router.push('/return-cases');
      } else {
        this.$router.push('/orders');
      }
    },
    getFabricDescription(productGroupNumber: string, productGroup: string, fabric: string): string {
      if (productGroupNumber) {
        return productGroupNumber;
      } else {
        return (productGroup || '') + (fabric || '');
      }
    },
    onOrderDateChange() {
      this.updateMatrix();
      this.emitOrdersValidation();
      this.showDatePicker = false;
      this.$emit('update:matrixOrders', this.updatedMatrixOrders);
    },
    setQuantityToProductOnSelectedMatrixOrder(product: MatrixProduct) {
      const clonedProduct = structuredClone(toRaw(product)) as MatrixProduct;
      const selectedMatrixProductIndex = this.selectedOrder.matrixProducts.findIndex(
        (matrixProduct) => matrixProduct.productId === product.productId,
      );

      if (selectedMatrixProductIndex !== -1) {
        this.selectedOrder.matrixProducts[selectedMatrixProductIndex].total = clonedProduct.total;
      } else if (product.total !== 0) {
        this.selectedOrder.matrixProducts.push({ ...product });
      }
    },
    async showProductModelDetailsPopup(productMatrix: MatrixProduct) {
      const imagesResult = await this.productImagesService.getProductModelImages(productMatrix.productGroupNumber);

      if (imagesResult.isValid() && imagesResult.model && imagesResult.model.length > 0) {
        this.showProductModelDetails = {
          name: productMatrix.productName,
          defaultVariantImages: imagesResult.model,
          defaultImage: imagesResult.model[0],
          mainGroup: productMatrix.mainGroup,
        };
      }
    },
    closeProductModelDetailsPopup() {
      this.showProductModelDetails = undefined;
    },
    isDark() {
      return this.userProfile.user?.theme === BackstageTheme.dark;
    },
    setStickyOffsets() {
      // set order is important given they depend on each others calculations
      this.setMatrixRelatedOrdersStickyOffset();
      this.setMainGroupGenderNavBarSitckyOffsetValue();
      this.setMatrixSizesBarSitckyOffsetValue();
    },
    setMatrixRelatedOrdersStickyOffset() {
      this.matrixRelatedOrdersStickyOffset = this.getMatrixRelatedOrdersStickyOffset();
    },
    setMainGroupGenderNavBarSitckyOffsetValue() {
      this.mainGroupGenderNavBarStickyOffset = this.getMainGroupGenderNavBarSitckyOffsetValue();
    },
    setMatrixSizesBarSitckyOffsetValue() {
      this.matrixSizesBarStickyOffset =
        this.getMainGroupGenderNavBarSitckyOffsetValue() +
        utilsMixins.calculateDivHeight(['#quantitiesNavBar']) +
        ((document.getElementsByClassName('product-group-header')[0] as HTMLElement | null)?.offsetHeight ?? 0);
    },
    getMatrixRelatedOrdersStickyOffset() {
      return utilsMixins.calculateDivHeight(['#addEditNavBar']);
    },
    getMainGroupGenderNavBarSitckyOffsetValue() {
      return (
        this.getMatrixRelatedOrdersStickyOffset() + utilsMixins.calculateDivHeight(['#matrixRelatedOrdersSelector'])
      );
    },
  },
});

export type ProductsQuantitiesSelectorRef = InstanceType<typeof ProductsQuantitiesSelector>;
export default ProductsQuantitiesSelector;
</script>

<style lang="scss">
.products-matrix {
  .is-invalid {
    border: none !important;
    color: var(--special-red-color-darker) !important;

    input::placeholder {
      color: var(--special-red-color-darker) !important;
    }
  }

  .orders-selector {
    &.selected {
      .vdp-datepicker {
        color: var(--primary-text-color);
      }
    }

    .form-group {
      position: relative;
    }

    .input-label {
      position: absolute;
      top: 50%;
      left: -25px;
      width: 16px;
      height: 15px;
      cursor: pointer;
      text-indent: -9999px;
      background-image: url('@/assets/img/ico-pencil.svg');
      background-position: center;
      background-size: contain;
      background-repeat: no-repeat;
      transform: translateY(-50%);
      margin-top: 0;
    }

    .vdp-datepicker__calendar {
      position: fixed;
      left: 50%;
      transform: translate(-50%, 50px);
    }

    .vdp-datepicker {
      input {
        background-color: transparent;
        cursor: pointer;
        background: transparent;
        border: none;
        font-size: 16px;
        height: auto;
        padding: 0;
        color: var(--primary-text-color);
        width: 90px;
      }
    }
  }
  .expand-image-btn {
    top: 5px;
    right: 5px;
    width: 24px;
    height: 24px;
    display: none;
    position: absolute;
    @include icon-image('@/assets/img/icons/expand.svg', var(--primary-text-color));

    &:hover {
      display: block;
    }
  }
}
.vdp-datepicker * {
  box-sizing: border-box;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
}
</style>
<style scoped lang="scss">
.products-matrix {
  max-width: unset;
  padding: 0;
  width: 100%;

  &.multiple {
    .shipping-dates-tabs {
      .tab {
        max-width: 225px;
      }
    }
  }

  .orders-selector {
    .action-icons {
      display: flex;
      gap: 15px;
      justify-content: flex-end;
      padding-top: 5px;

      .clone-order,
      .delete-order {
        width: 20px;
        height: 20px;
        opacity: 0.5;
        transition:
          transform 0.2s linear,
          opacity 0.2s linear;

        &:hover {
          opacity: 1;
          transform: translateY(-20%) scale(1.05);
        }
      }
    }
  }

  .warning-banner {
    padding: 20px;
    margin-bottom: 20px;
    color: var(--primary-text-color);
    background-color: var(--special-orange-color);

    @include breakpoint(medium) {
      margin-top: 10px;
      margin-bottom: 10px;
    }
  }

  .products:deep() {
    .dates-selector-cnt {
      background-color: var(--primary-bg-color);
      border-top: 1px solid var(--bg-page-color);
      position: sticky;
      z-index: 4;
      top: 83px;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;

      @include breakpoint(small) {
        padding: 0;
      }

      @include breakpoint(medium) {
        top: 63px;
      }

      @include breakpoint(small) {
        top: 173px;
      }
    }

    .products-quantities-order-date-cnt {
      position: absolute;
      top: 100%;
      z-index: 3;
      left: 0px;
    }

    .navbar-background {
      @include navbar-background;

      top: 83px;

      @include breakpoint(medium) {
        top: 63px;
      }

      @include breakpoint(small) {
        top: 173px;
      }

      &.multiple {
        top: 168.8px;
      }
    }

    .main-groups-nav-bar {
      @include common-container;
      padding: 12px 20px;
      border-radius: 0;

      @include breakpoint(medium) {
        padding-bottom: 10px;
      }
    }

    .product-groups-cnt {
      @include common-container;
      padding: 16px 20px;

      .product-group-cnt {
        margin-bottom: 10px;
        background-color: var(--primary-bg-color);

        .product-group-body {
          .matrix-table {
            max-width: 100%;
            display: block;

            .row {
              display: flex;
              width: 100%;
              max-width: 100%;
              border-bottom: 1px solid var(--border-color-new);
              height: 70px;
              box-sizing: content-box;
              overflow: hidden;

              &.header {
                position: sticky;
                z-index: 1;
                background: var(--primary-bg-color);

                .first-column {
                  justify-content: flex-end;

                  .size-selector-cnt {
                    height: 100%;
                    border-right: 1px solid var(--border-color-new);
                    margin-right: 81px;

                    .size-selector {
                      text-align: left;
                      background-color: var(--field-bg-color);
                      margin: 6px 18px 0 0;
                      text-transform: uppercase;
                      padding: 8px 0px;
                      border-radius: 4px;

                      .allways-size {
                        padding-left: 16px;
                        color: var(--primary-text-remark-color);
                        font-size: 12px;
                        word-break: keep-all;
                      }

                      select {
                        font-size: 14px;
                        font-weight: 500;
                        height: auto;
                        padding: 5px 55px 5px 15px;
                        background-color: var(--field-bg-color);
                        color: var(--primary-text-color);
                        text-transform: uppercase;
                        cursor: pointer;
                        border: none;
                      }
                    }
                  }
                }
              }

              @include breakpoint(medium) {
                height: auto;

                &.row .first-column {
                  .size-selector-cnt {
                    margin-right: 0;
                    border-right: 0;
                    height: auto;

                    .size-selector {
                      margin-right: 0px;
                      margin-top: 0px;

                      select {
                        padding: 5px 19px 5px 15px;
                        background-position: right calc(15px - 1em) center;
                      }
                    }
                  }
                }
              }

              &.pre-order-disabled {
                position: relative;

                .column {
                  opacity: 0.3;
                }

                .pre-order-disabled-legend {
                  width: 100%;
                  position: absolute;
                  opacity: 1;
                  line-height: 100%;
                  text-align: center;
                  padding: 0 10px;
                  color: var(--primary-text-remark-color);
                  height: 70px;
                  display: flex;
                  align-items: center;

                  p {
                    margin: 0 auto;
                    font-weight: 500;
                    background: rgba(255, 255, 255, 0.75);
                    width: 400px;
                    font-size: 0.9em;
                  }
                }
              }
            }

            .column {
              width: 100%;
              padding: 0;
              border-left: 1px solid var(--border-color-new);
              display: flex;
              position: relative;
              flex-wrap: wrap;
              align-content: center;

              &.first-column {
                display: flex;
                min-width: 360px;
                width: 360px;
                padding: 0;
                align-items: center;
                flex-flow: row;

                .product-fabric-text p {
                  margin: 0;
                  font-size: 14px;
                  text-align: left;
                  color: var(--primary-text-remark-color);
                }

                .product-launch-text {
                  margin-left: auto;
                  padding: 17px 10px;
                  min-width: 82px;
                  border-left: 1px solid var(--bg-page-color);

                  p {
                    margin: 0;
                    text-align: center;
                    margin-left: auto;
                    font-size: 14px;
                    line-height: 150%;
                    color: var(--primary-text-remark-color);
                  }
                }

                .product-launch-text p.strong,
                .product-fabric-text p.strong {
                  color: var(--primary-text-color);
                  font-weight: 700;
                  font-size: 16px;
                }
              }

              &.last-column {
                min-width: 80px;
                width: 80px;
                border-left: none;
                background-color: var(--bg-remark-color);

                span.strong {
                  line-height: 70px;
                  display: block;
                  width: 100%;
                }
              }

              .product {
                width: 100%;

                .product-header {
                  width: 100%;
                  height: 20px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  top: 0;
                  position: absolute;
                  color: var(--primary-text-color);
                  background-color: var(--special-gray-color);
                  font-size: clamp(7px, 0.55vw, 10px);
                  text-transform: uppercase;
                  letter-spacing: -0.02em;
                  font-family: 'On Semi Mono';
                  padding-top: 1px;

                  &.special-green {
                    background-color: var(--special-green-color);
                  }
                  &.special-orange {
                    background-color: var(--special-orange-color);
                  }
                  &.special-red {
                    background-color: var(--special-red-color);
                  }
                  &.red {
                    background-color: var(--red-color);
                  }

                  span {
                    overflow: hidden;
                    padding: 0 2px;
                  }

                  + .product-total {
                    height: 54px;
                    margin-top: 16px;
                  }
                }

                .product-total {
                  height: 70px;
                  width: 100%;

                  input {
                    padding: 0;

                    &:focus::placeholder {
                      color: transparent;
                    }
                  }
                }
              }

              .size-cnt {
                width: 100%;

                div {
                  margin-bottom: 5px;
                  font-size: 16px;
                  font-weight: 700;

                  &:last-child {
                    margin-bottom: 0;
                  }

                  &.us {
                    font-weight: 400;
                    font-size: 14px;
                    color: var(--primary-text-remark-color);
                  }
                }
              }

              .product-fabric-img {
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
                height: 80px;
                width: 110px;
                cursor: pointer;

                .expand-image-btn {
                  display: block;
                }

                @include breakpoint(medium) {
                  .expand-image-btn {
                    width: 15px;
                    height: 15px;
                    top: 0;
                    right: 0;
                  }
                }

                img {
                  display: block;
                  max-width: 80px;
                  max-height: 70px;
                  width: auto;
                  height: auto;
                  object-fit: contain;
                  margin: 0px 15px;
                }
              }

              input[type='number'] {
                border: none;
                width: 100%;
                text-align: center;
                outline-width: 0;
                color: var(--primary-text-color);
                font-size: 16px;
                min-height: 100%;
                box-sizing: border-box;
              }

              @media (max-width: 1000px) {
                &.first-column {
                  min-width: 245px;
                  width: 245px;

                  img {
                    margin: 0 5px;
                  }

                  .product-fabric-text {
                    word-break: break-word;
                    padding: 0;
                    margin: 0 5px;
                    min-width: auto;

                    p {
                      font-size: 10px;
                    }
                  }

                  .product-launch-text {
                    padding: 0;
                    margin: 0 5px;
                    min-width: auto;

                    p {
                      font-size: 10px;
                    }
                  }
                }

                &.last-column {
                  min-width: 55px;
                  width: 55px;
                }

                input[type='number'] {
                  font-size: 12px;
                }
              }

              @include breakpoint(medium) {
                &.first-column {
                  min-width: 80px;
                  width: 80px;
                  display: block;

                  img {
                    width: 60px;
                    height: 35px;
                    margin: 0;
                    text-align: left;
                  }

                  .product-launch-text {
                    padding: 0;
                    min-width: auto;
                  }

                  .product-launch-text p,
                  .product-fabric-text p {
                    font-size: 10px;
                    text-align: left;
                  }

                  .product-launch-text p.strong,
                  .product-fabric-text p.strong {
                    font-size: 10px;
                    text-align: left;
                  }
                }

                &.last-column {
                  min-width: 35px;
                  width: 35px;
                }

                .size-cnt {
                  div {
                    font-size: 10px;

                    &.us {
                      font-size: 9px;
                    }
                  }
                }
              }
            }
          }
        }

        @include breakpoint(medium) {
          font-size: 14px;
          padding: 0;
        }
      }

      @include breakpoint(medium) {
        padding: 20px 2px;
      }

      &.availability .product-header {
        height: 100% !important;
      }
    }
  }

  .total-all-cnt {
    border-top: 1px solid var(--border-color);
  }

  &.availability {
    .products {
      .product-groups-cnt .product-group-cnt .product-group-body table td {
        .product-total {
          display: none;
        }

        .product-header {
          height: --webkit-fill-available;
          height: --moz-available;
          height: stretch;
          span {
            font-weight: 600;
          }
        }
      }

      .totals-cnt {
        margin-bottom: 20px;
        .totals {
          display: none;
        }
      }

      .buttons-cnt {
        .button.continue {
          display: none;
        }
      }
    }
  }
}
</style>
